// src/components/ui/SmartLink.jsx
//
// A real <a href> that still navigates client-side.
//
// WHY THIS EXISTS
// The marketing pages navigated exclusively through `<button onClick={navigate(...)}>`.
// That works for humans and is invisible to crawlers: a Googlebot fetch of
// https://logami.app/en found exactly ONE <a href> on the whole page (a mailto:),
// so the strongest page on the domain (GSC position 3.7) passed zero link equity to
// /mock-exams, /try, /telc-b1 or the blog. See
// doc/SEO_MOCK_EXAM_RANKING_DIAGNOSIS_2026-08.md §3.
//
// SmartLink emits the crawlable href AND keeps SPA navigation (no full reload), and
// preserves native browser behaviour for middle-click / cmd-click / ctrl-click so
// "open in new tab" works — which plain onClick buttons also silently broke.
//
// `to` is lang-relative ('/pricing'), matching useLocalizedNavigate. The rendered
// href is absolute-with-lang ('/en/pricing') so crawlers resolve it correctly.

import React, { useCallback } from 'react';
import { useLocalizedNavigate, useLangParam } from '../../hooks/useLocalizedNavigate';

export default function SmartLink({ to, children, onClick, ...rest }) {
  const navigate = useLocalizedNavigate();
  const lang = useLangParam();

  const path = to.startsWith('/') ? to : `/${to}`;
  const href = path === '/' ? `/${lang}` : `/${lang}${path}`;

  const handleClick = useCallback(
    (e) => {
      // Let the browser handle new-tab / new-window / download intents natively.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      e.preventDefault();
      onClick?.(e); // analytics/tracking callers pass through here
      navigate(to);
    },
    [navigate, onClick, to]
  );

  return (
    <a href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
